
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Media } from "@/components/media/types";
import { toastService } from "@/utils/toastService";
import { extractVideoThumbnail } from "@/components/media/utils/thumbnailExtractor";

interface PreviewUploadOptions {
  memorialId: string;
  storyId?: string;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB for images/audio
const MAX_VIDEO_FILE_SIZE = 200 * 1024 * 1024; // 200MB for videos (Cloudflare Stream)

export const usePreviewMediaUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadFileForPreview = async (
    file: File, 
    options: PreviewUploadOptions
  ): Promise<Media> => {
    const maxSize = file.type.startsWith('video/') ? MAX_VIDEO_FILE_SIZE : MAX_FILE_SIZE;
    if (file.size > maxSize) {
      throw new Error(`${file.name} is too large. Maximum size is ${file.type.startsWith('video/') ? '200MB' : '50MB'}`);
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Generate a unique filename for storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      
      // Store in temporary location for preview
      const filePath = options.storyId 
        ? `temp-stories/${options.storyId}/${fileName}`
        : `temp-memorials/${options.memorialId}/${fileName}`;

      setUploadProgress(50);

      // Extract thumbnail for videos if needed
      let thumbnailUrl = null;
      if (file.type.startsWith('video/')) {
        try {
          const thumbnailDataUrl = await extractVideoThumbnail(file);
          if (thumbnailDataUrl) {
            thumbnailUrl = thumbnailDataUrl; // Keep as data URL for preview
          }
        } catch {
          // Thumbnail extraction failed, continuing without thumbnail
        }
      }

      // Upload to storage in temporary location
      const { error: uploadError } = await supabase.storage
        .from('memorials')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: true
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      setUploadProgress(100);

      // Get the public URL for preview
      const { data: { publicUrl } } = supabase.storage
        .from('memorials')
        .getPublicUrl(filePath);

      // Determine media type
      const mediaType = file.type.startsWith('image/') 
        ? 'image' 
        : file.type.startsWith('video/') 
          ? 'video' 
          : 'audio';

      // Return preview media object (NO database record created)
      const previewMedia: Media = {
        id: `preview-${Date.now()}-${Math.random()}`, // Temporary ID
        url: publicUrl,
        media_type: mediaType as 'image' | 'video' | 'audio',
        memorial_id: options.memorialId,
        temp_file: file,
        thumbnail_url: thumbnailUrl,
        temp_storage_path: filePath // Track for cleanup
      };

      return previewMedia;
    } catch (error: unknown) {
      console.error('[usePreviewMediaUpload] Preview upload failed:', error);
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const cleanupPreviewMedia = async (media: Media[]) => {
    const cleanupPromises = media
      .filter(m => m.id.startsWith('preview-') && m.temp_storage_path)
      .map(async (m) => {
        try {
          await supabase.storage
            .from('memorials')
            .remove([m.temp_storage_path!]);
          
          // Also cleanup object URLs
          if (m.url.startsWith('blob:')) {
            URL.revokeObjectURL(m.url);
          }
          if (m.thumbnail_url && m.thumbnail_url.startsWith('blob:')) {
            URL.revokeObjectURL(m.thumbnail_url);
          }
        } catch (error) {
          console.error('[usePreviewMediaUpload] Failed to cleanup preview media:', m.id, error);
        }
      });

    await Promise.allSettled(cleanupPromises);
  };

  return {
    uploadFileForPreview,
    cleanupPreviewMedia,
    isUploading,
    uploadProgress
  };
};
